import { useCallback } from 'react';
import { MAX_FILE_SIZE_ALLOWED_IN_MB } from 'common/constants/documents';
import { FILE_TYPE_NOT_ALLOWED_TRY_AGAIN, MAX_FILE_SIZE_ALLOWED } from 'common/constants/user';
import defaultValidTypes from 'components/Documents/validFileTypes';
import { useDocuments, useResponse } from 'services/hooks';
import { UploadMultipleFilesProps, UseHandleFilesProps } from './types';

const useHandleFiles = (props: UseHandleFilesProps) => {
  const { maxFileSizeAllowed = MAX_FILE_SIZE_ALLOWED_IN_MB, validFileTypes = defaultValidTypes } = props;
  const { errorNotification } = useResponse();
  const { uploadRequestedFiles, progress } = useDocuments();

  const validateFiles = useCallback(
    (files: FileList): boolean =>
      Array.from(files).every(file => {
        const fileSize = file.size / 1024 / 1024; // Convert file size to MB
        const extension = file.name.split('.').pop() ?? '';

        // Check if the file is too large
        if (fileSize > maxFileSizeAllowed) {
          errorNotification(MAX_FILE_SIZE_ALLOWED(maxFileSizeAllowed));
          return false; // Exit and return false on the first invalid file
        }

        // Check if the file type is valid
        if (!validFileTypes.includes(extension.toLowerCase())) {
          errorNotification(FILE_TYPE_NOT_ALLOWED_TRY_AGAIN(file.name));
          return false; // Exit and return false on the first invalid file
        }

        return true; // File is valid
      }),
    [errorNotification, maxFileSizeAllowed, validFileTypes]
  );

  // Simple function to upload multiple files in parallel
  const uploadMultipleFiles = useCallback(
    async (props: UploadMultipleFilesProps) => {
      const { files, task, requestedFileId, setUploadedFiles, companyDocuments, showNotifications = true } = props;
      try {
        // Create an array of promises to upload all files in parallel
        const uploadPromises = Array.from(files).map(file =>
          uploadRequestedFiles({
            droppedFile: file,
            task,
            requestedFilesIds: [requestedFileId],
            setUploadedFiles,
            files: companyDocuments,
            showNotifications,
          })
        );

        // Wait for all file uploads to complete
        await Promise.all(uploadPromises);
      } catch (error) {
        return error;
      }
    },
    [uploadRequestedFiles]
  );

  return {
    progress,
    validateFiles,
    uploadMultipleFiles,
  };
};

export default useHandleFiles;
